// Angular imports
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';

// 3rd party imports
import { DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import {
  CCDeviceService,
  IconConstants,
  RxjsUtils
} from '@therealmanage/ciranet-ui-common';

// Local component imports
import { ResidentImageEditorComponent } from '../../shared-components/image-editor/image-editor.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// Model imports
import { UserInfoResultDto } from '../../_models/user-info-result-dto';
import { UserProfileImageParameterDto } from '../../_models/user-profile-image-parameter-dto';

// Constants
import { MessageInterval } from '../../_constants/messages-intervals';

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  standalone: false
})
export class EditProfileComponent implements OnInit {
  @ViewChild('editProfilePopup', { static: false })
  editProfilePopup: DxPopupComponent;
  @ViewChild('profileImageEditor', { static: false })
  profileImageEditor: ResidentImageEditorComponent;

  loadingVisible = false;
  popupVisible = false;
  isSupportEditImage = true; // EnumerateDevices() not supported or found
  isFirstSelectImage = true;
  isSmallScreen = false;
  isLandscape: boolean = false;

  allowProfileEdit = true;
  allowRemoveProfileImage = false;
  profileImageEdited = false;
  profileImageRemoved = false;

  title: string;
  userProfileImage: UserProfileImageParameterDto;
  cancelButtonIcon = IconConstants.CANCEL;
  saveButtonIcon = IconConstants.SAVE;
  editButtonIcon = IconConstants.EDIT;

  constructor(
    private authService: AuthService,
    private screenService: ScreenService,
    private errorService: ServerErrorService,
    private deviceService: CCDeviceService,
    private userService: UserService,
    private helperService: HelperService,
    private cdr: ChangeDetectorRef
  ) {
    //Commenting for now to deploy on dev and test on true devices to see what error is caused.
    //this.isSupportEditImage =
    //this.screenService.isXSmallScreen() === false && !(this.isChrome === true && this.isIPhone);
  }

  ngOnInit() {
    this.loadProfileImage();
    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectImpersonateChanged(),
      () => this.loadProfileImage()
    );

    this.isSmallScreen = this.screenService.isSmallScreen();

    RxjsUtils.safeSubscribe(this.screenService.changed, () => {
      this.isSmallScreen = this.screenService.isSmallScreen();
    });

    this.isLandscape = this.deviceService.isLandscape();
    RxjsUtils.subscribeToSubject(
      this.deviceService.subjectOrientationChanged,
      () => {
        this.isLandscape = this.deviceService.isLandscape();
      }
    );
  }

  show() {
    this.popupVisible = true;
    this.userProfileImage = new UserProfileImageParameterDto();
    const userInfo: UserInfoResultDto = this.userService.getCurrentUserInfo();
    if (userInfo === null) {
      notify(
        'There is an issue retrieving user info. Please re-sign in and try again',
        'error',
        MessageInterval.defaultErrorMsgInterval
      );
    } else {
      this.title = `Edit Profile - ${userInfo.displayName}`;
      if (!!userInfo.profileImage) {
        this.userProfileImage.imageBase64 = userInfo.profileImage;
        this.allowRemoveProfileImage = true;
      }
    }
  }

  hide(): void {
    this.onCloseClick();
    this.popupVisible = false;
  }

  repaint(): void {
    this.editProfilePopup.instance.repaint();
  }

  hasProfileImage(): boolean {
    return !!this.userProfileImage && !!this.userProfileImage.imageBase64;
  }

  getProfileImage(): string {
    if (this.hasProfileImage()) {
      return this.userProfileImage.imageBase64;
    } else {
      return 'assets/images/avatar_question_round.png';
    }
  }

  onProcessProfileImageClick = () => {
    if (this.allowProfileEdit) {
      this.onShowProfileImageEditorClick();
    }
  };

  onShowProfileImageEditorClick(): void {
    if (this.isSupportEditImage) {
      this.profileImageEditor.show();
      //fix button issue position in edge
      if (this.deviceService.isEdge() && this.isFirstSelectImage) {
        setTimeout(() => {
          this.profileImageEditor.hide();
          setTimeout(() => {
            this.profileImageEditor.show();
            this.isFirstSelectImage = false;
          }, 200);
        }, 100);
      }
    } else {
      notify("Your device / browser doesn't support image editing!");
    }
  }

  imageCropped(data): void {
    if (!!data) {
      // always return base64 image data
      this.userProfileImage.imageBase64 = data;
      this.profileImageEdited = true;
      this.profileImageRemoved = true;
      this.profileImageEditor.hide();
      this.cdr.detectChanges();
    }
  }

  onRemoveImageClick = () => {
    const dialog = this.helperService.getConfirmationDialog(
      'Warning',
      'Are you sure you want to remove image? It is not effective until you hit the save button.',
      'Yes',
      'No',
      'danger',
      'default'
    );
    dialog.show().then((dialogResult) => {
      if (dialogResult === true) {
        this.userProfileImage.imageBase64 = null;
        this.profileImageEdited = true;
        this.profileImageRemoved = true;
        this.allowRemoveProfileImage = false;
      }
    });
  };

  onSaveClick() {
    this.loadingVisible = true;
    RxjsUtils.safeSubscribe(
      this.userService.updateProfile(this.userProfileImage),
      (response) => {
        notify(
          'Update has been saved successfully',
          'success',
          MessageInterval.defaultErrorMsgInterval
        );
        this.loadingVisible = false;

        const userInfo = this.userService.getCurrentUserInfo();
        if (userInfo != null) {
          userInfo.profileImage = this.userProfileImage.imageBase64;
          this.userService.setCurrentUserInfo(userInfo);
        }

        // notify the user menu on the top right corner
        this.authService.updateSubjectProfileImageUpdated(
          this.userProfileImage.imageBase64
        );

        // reset all variables and keep the popup open
        this.onCloseClick();
        this.show();
      },
      (error) => {
        this.loadingVisible = false;
        this.errorService.displayError(error);
      }
    );
  }

  onCloseClick() {
    this.allowProfileEdit = true;
    this.allowRemoveProfileImage = false;
    this.profileImageEdited = false;
    this.profileImageRemoved = false;
    this.userProfileImage = null;
  }

  private loadProfileImage(): void {
    this.userProfileImage = new UserProfileImageParameterDto();
    const userInfo: UserInfoResultDto = this.userService.getCurrentUserInfo();
    if (!!userInfo.profileImage) {
      this.userProfileImage.imageBase64 = userInfo.profileImage;
    }
  }
}
